/* ════════════ THE BOOT LOG ════════════
   The linux page's atmosphere: an endless kernel boot scrolling up
   the screen — dmesg lines, [ OK ] units, one telemetry.service
   that correctly fails to start. Plus pixel Tux in the hero hud
   and an uptime chip counting from the 1991 usenet post. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

const GREEN = "#57ff7a";
const AMBER = "#ffd23e";
const MAGENTA = "#ff2e88";
const GREY = "#7d8f82";

/* ───────── background: dmesg forever ───────── */
function dmesg(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#dmesg")!;
  const ctx = c.getContext("2d")!;

  // a boot that never finishes booting — the log loops, shuffled
  const OK = (s: string) => ({ tag: "[  OK  ] ", col: GREEN, s });
  const RAW = (s: string) => ({ tag: "", col: GREY, s });
  const LINES = [
    RAW("[    0.000000] Linux version 6.12.0-freedom (you@yourmachine) (gcc 14.2)"),
    RAW("[    0.000000] Command line: root=/dev/freedom ro quiet kings=none"),
    RAW("[    0.042133] Memory: all of it yours (0K reserved for advertising)"),
    RAW("[    0.133700] tux: waddle engaged. flippers nominal."),
    RAW("[    0.271828] random: crng init done (entropy: politics)"),
    RAW("[    0.314159] audit: type=1991 the license is the firewall"),
    OK("Mounted /home (LUKS encrypted)"),
    OK("Started Load Kernel Modules"),
    OK("Reached target Sovereignty"),
    OK("Started tor.service — the envelope, sealed"),
    OK("Started gnu.service (since 1983)"),
    OK("Activated swap on /dev/conscience"),
    { tag: "[ FAIL ] ", col: MAGENTA, s: "telemetry.service: unit not found (this is correct)" },
    { tag: "[ SKIP ] ", col: AMBER, s: "ads.target skipped — no vendor present" },
    RAW("systemd[1]: Startup finished in 1.337s (kernel) + 0s (asking permission)"),
    RAW("login: root means root. talk is cheap, show me the code."),
    RAW("$ uptime — up since august 25, 1991. no kings detected."),
  ];

  type Row = { line: (typeof LINES)[number]; y: number };
  let rows: Row[] = [];
  let lineH = 18;
  let next = 0;

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    lineH = Math.max(16, Math.floor(innerHeight / 42));
    rows = [];
    next = (Math.random() * LINES.length) | 0;
    for (let y = c.height; y > -lineH; y -= lineH) {
      rows.unshift({ line: LINES[next++ % LINES.length]!, y });
    }
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.font = '12px "IBM Plex Mono", monospace';
    ctx.textBaseline = "top";
    for (const r of rows) {
      // brightest at the bottom (freshest), fading as it scrolls into history
      const p = r.y / c.height;
      const a = 0.04 + Math.max(0, p) * 0.16;
      ctx.globalAlpha = a;
      ctx.fillStyle = r.line.col;
      ctx.fillText(r.line.tag, 24, r.y);
      ctx.fillStyle = r.line.tag ? GREY : r.line.col;
      ctx.globalAlpha = a * (r.line.tag ? 0.9 : 1);
      ctx.fillText(r.line.s, 24 + ctx.measureText(r.line.tag).width, r.y);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }

  const tick = () => {
    if (signal.aborted) return;
    for (const r of rows) r.y -= 0.35;
    const last = rows[rows.length - 1]!;
    if (last.y < c.height - lineH) {
      rows.push({ line: LINES[next++ % LINES.length]!, y: last.y + lineH });
    }
    while (rows.length && rows[0]!.y < -lineH) rows.shift();
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

/* ───────── hero: pixel tux ───────── */
function tux(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#tux")!;
  const ctx = c.getContext("2d")!;

  // 12×17 sprite: B body, W belly, E eye-white, O beak/feet
  const SPRITE = [
    "...BBBBBB...",
    "..BBBBBBBB..",
    "..BBBBBBBB..",
    "..BEEBBEEB..",
    "..BEBBBBEB..",
    "..BBOOOOBB..",
    "..BBBOOBBB..",
    ".BBBWWWWBBB.",
    ".BBWWWWWWBB.",
    "BBBWWWWWWBBB",
    "BB.WWWWWW.BB",
    "BB.WWWWWW.BB",
    ".B.WWWWWW.B.",
    "..BWWWWWWB..",
    "..BBWWWWBB..",
    ".OOOB..BOOO.",
    "OOOO....OOOO",
  ];
  const COLS: Record<string, string> = {
    B: "#10131a",
    W: "#e8ecef",
    E: "#ffffff",
    O: "#f7a41d",
  };

  const cell = Math.floor(Math.min(c.width / 12, c.height / SPRITE.length));
  const ox = (c.width - cell * 12) / 2;
  const oy = (c.height - cell * SPRITE.length) / 2;

  const draw = (blink: boolean) => {
    ctx.clearRect(0, 0, c.width, c.height);
    SPRITE.forEach((row, ry) => {
      [...row].forEach((ch, rx) => {
        if (ch === ".") return;
        // a blink: eye-whites go body-black for a few frames
        ctx.fillStyle = blink && ch === "E" ? COLS.B! : COLS[ch]!;
        ctx.fillRect(ox + rx * cell, oy + ry * cell, cell, cell);
      });
    });
    // body outline glow so tux reads against the dark
    ctx.strokeStyle = "rgba(87, 255, 122, 0.25)";
    ctx.strokeRect(ox - 2, oy - 2, cell * 12 + 4, cell * SPRITE.length + 4);
  };

  draw(false);
  if (REDUCED) return;
  // penguins blink. rarely, like commits to a stable branch.
  const blinker = setInterval(() => {
    draw(true);
    setTimeout(() => draw(false), 140);
  }, 4200 + Math.random() * 2400);
  signal.addEventListener("abort", () => clearInterval(blinker));
}

/* ───────── uptime chip: counting since the usenet post ───────── */
function uptime() {
  const el = document.querySelector<HTMLElement>("#uptime");
  if (!el) return;
  const birth = Date.UTC(1991, 7, 25); // august 25, 1991
  const days = Math.floor((Date.now() - birth) / 86_400_000);
  el.textContent = `▓ uptime: ${days.toLocaleString("en-US")} days · 0 reboots of the idea`;
}

onPage((signal) => {
  dmesg(signal);
  tux(signal);
  uptime();
});
